/**
 * Pendências financeiras (contas a receber) — regras puras.
 *
 * Uma pendência é DERIVADA da cobrança do agendamento, não um doc à parte.
 *
 * QUEM deve: o convênio sempre que o agendamento tem convênio (`insurerId`)
 * — mesmo com a cobrança lançada como "A pagar" —; senão, o paciente.
 *
 * QUANDO fecha (pelo status da cobrança):
 *   - PENDENTE / PARCIAL → deve o saldo (`remainingBalance`). Fecha quando
 *     vira PAGO — pela baixa aqui, pelo "Salvar cobrança" na agenda ou pelo
 *     webhook do Pagar.me. Nada a sincronizar: é o mesmo campo.
 *   - CONVENIO ("Convênio paga") → deve `insurerAmount`. Sem ele o valor
 *     fica "a definir" — de propósito NÃO cai no `expectedPrice`: a tabela
 *     de preços ainda não tem os valores de convênio (e o que ela sugere hoje
 *     é o particular). Fecha com `receivableSettledAt`.
 *
 * Só conta cobrança lançada a partir de `RECEIVABLES_START_MS`: antes disso
 * a recepção não registrava cobrança nesse formato (convênio com valor,
 * "a pagar" acompanhado), e o legado entupiria a fila.
 *
 * Só vira pendência o que "não fechou no dia": atendimento de dia anterior
 * a hoje (o de hoje ainda pode ser pago no balcão) — ou já vencido pela
 * data prevista. Cancelado, falta e desmarcado não geram dívida.
 */
import type { Appointment } from '@/types/models';
import {
  APPOINTMENT_TYPE_LABEL,
  procedureSummary,
  remainingBalance,
} from '@/utils/appointmentCharge';

export type ReceivableDebtor = 'PACIENTE' | 'CONVENIO';

/** Início do controle de pendências financeiras (24/09/2026, 00:00 BRT).
 *  Cobrança lançada (`chargedAt`) antes disso não vira pendência. */
export const RECEIVABLES_START_MS = Date.parse('2026-09-24T00:00:00-03:00');

const owesStatus = (a: Appointment) =>
  a.paymentStatus === 'PENDENTE' || a.paymentStatus === 'PARCIAL' || a.paymentStatus === 'CONVENIO';

/** Quem deve (ou devia, se já teve baixa) — null quando a cobrança não gera
 *  dívida. Convênio no agendamento = o convênio é o devedor. */
export function receivableDebtor(a: Appointment): ReceivableDebtor | null {
  if (!owesStatus(a) && !a.receivableSettledAt) return null;
  return a.insurerId || a.paymentStatus === 'CONVENIO' ? 'CONVENIO' : 'PACIENTE';
}

/** Valor que o convênio deve (undefined = a definir). */
export function insurerDue(a: Appointment): number | undefined {
  return typeof a.insurerAmount === 'number' ? a.insurerAmount : undefined;
}

/** Valor em aberto da pendência (undefined = convênio sem valor definido).
 *  "A pagar" com convênio usa o valor lançado na cobrança, a menos que o
 *  combinado tenha fixado outro `insurerAmount`. */
export function openAmount(a: Appointment): number | undefined {
  if (a.paymentStatus === 'CONVENIO') return insurerDue(a);
  if (a.paymentStatus === 'PENDENTE' || a.paymentStatus === 'PARCIAL') {
    return receivableDebtor(a) === 'CONVENIO' ? insurerDue(a) ?? remainingBalance(a) : remainingBalance(a);
  }
  return 0;
}

/** A cobrança gera uma dívida ainda em aberto? (independe do dia) */
export function isOpenReceivable(a: Appointment): boolean {
  if (a.isDeleted || a.status === 'CANCELADA' || a.status === 'FALTOU') return false;
  if ((a.chargedAt ?? 0) < RECEIVABLES_START_MS) return false;
  if (a.paymentStatus === 'PENDENTE' || a.paymentStatus === 'PARCIAL') return remainingBalance(a) > 0.001;
  if (a.paymentStatus === 'CONVENIO') return !a.receivableSettledAt;
  return false;
}

/** Meia-noite local de hoje (ms). */
export function startOfTodayMs(now = Date.now()): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Venceu: data prevista já passou (antes de hoje). */
export function isPastDue(a: Appointment, now = Date.now()): boolean {
  return typeof a.paymentDueDate === 'number' && a.paymentDueDate < startOfTodayMs(now);
}

/**
 * Entra na Caixa de Pendências: dívida em aberto de um dia que já acabou.
 * Atendimento de hoje fica de fora (ainda pode pagar no balcão); futuro
 * também ("a pagar" lançado na marcação não é dívida antes da consulta).
 */
export function isFinancialPendencia(a: Appointment, now = Date.now()): boolean {
  return isOpenReceivable(a) && (a.date < startOfTodayMs(now) || isPastDue(a, now));
}

/** "Consulta", "Topografia + Paquimetria", "Exame · OCT"… */
export function receivableProcedureLabel(a: Appointment): string {
  const detail = procedureSummary(a);
  if (detail) return detail;
  const base = APPOINTMENT_TYPE_LABEL[a.type] ?? a.type;
  return a.examType ? `${base} · ${a.examType}` : base;
}

/** Nome do devedor pra exibição. */
export function debtorLabel(a: Appointment, insurerNames: Record<string, string>): string {
  if (receivableDebtor(a) === 'CONVENIO') {
    return a.insurerId ? insurerNames[a.insurerId] ?? 'Convênio' : 'Convênio (não informado)';
  }
  return a.patientName || 'Paciente';
}

/** Chave de agrupamento por devedor: patientId ou `ins:<insurerId>`. */
export function debtorKey(a: Appointment): string {
  return receivableDebtor(a) === 'CONVENIO' ? `ins:${a.insurerId ?? '?'}` : `pac:${a.patientId}`;
}

/** Ordem padrão da ficha: vencidos primeiro, depois atendimento mais antigo. */
export function compareReceivables(a: Appointment, b: Appointment, now = Date.now()): number {
  const ad = isPastDue(a, now) ? 0 : 1;
  const bd = isPastDue(b, now) ? 0 : 1;
  if (ad !== bd) return ad - bd;
  return (a.date ?? 0) - (b.date ?? 0);
}

// ─── Ordenação da tabela ───────────────────────────────────────────

export type ReceivableSortKey = 'date' | 'charged' | 'due' | 'amount' | 'settled';
export interface ReceivableSort {
  key: ReceivableSortKey;
  dir: 'asc' | 'desc';
}

/** Valor usado pra ordenar. Sem valor (sem previsão, valor a definir)
 *  vai sempre pro fim, qualquer que seja a direção. */
function sortValue(a: Appointment, key: ReceivableSortKey): number | undefined {
  switch (key) {
    case 'date':
      return a.date;
    case 'charged':
      return a.chargedAt;
    case 'due':
      return a.paymentDueDate;
    case 'amount':
      return a.receivableSettledAt ? a.receivableSettledAmount : openAmount(a);
    case 'settled':
      return a.receivableSettledAt;
  }
}

export function sortReceivables(list: Appointment[], sort: ReceivableSort): Appointment[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((x, y) => {
    const vx = sortValue(x, sort.key);
    const vy = sortValue(y, sort.key);
    if (vx === undefined && vy === undefined) return (x.date ?? 0) - (y.date ?? 0);
    if (vx === undefined) return 1;
    if (vy === undefined) return -1;
    if (vx !== vy) return (vx - vy) * sign;
    // Empate (mesmo dia): horário do atendimento, na mesma direção.
    return (x.startTime ?? '').localeCompare(y.startTime ?? '') * sign;
  });
}

// ─── Exportação CSV ────────────────────────────────────────────────

const csvCell = (v: string | number | undefined | null): string => {
  if (v === undefined || v === null) return '';
  const t = String(v);
  return /[";\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};
const csvDate = (ms: number | undefined) => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};
/** Número no formato do Excel pt-BR: vírgula decimal, sem milhar. */
const csvMoney = (v: number | undefined) => (v === undefined ? '' : v.toFixed(2).replace('.', ','));

/**
 * CSV (separador `;`, pro Excel em português abrir em colunas) da lista
 * como está filtrada/ordenada na tela. Inclui BOM pra acentos saírem certos.
 */
export function receivablesToCsv(
  items: Appointment[],
  mode: 'open' | 'settled',
  insurerNames: Record<string, string>,
  profNames: Record<string, string> = {},
  methodLabel: (m: string | undefined) => string = (m) => m ?? '',
): string {
  const head = [
    'Paciente',
    'Data do atendimento',
    'Hora',
    'Referente a',
    'Devedor',
    'Convênio',
    'Profissional',
    'Lançado em',
    'Lançado por',
    ...(mode === 'open'
      ? ['Previsto para', 'Forma combinada', 'Valor em aberto (R$)']
      : ['Recebido em', 'Forma', 'Baixa por', 'Valor recebido (R$)']),
    'Observação',
  ];
  const rows = items.map((a) => {
    const ins = receivableDebtor(a) === 'CONVENIO';
    const insurer = a.insurerId ? insurerNames[a.insurerId] ?? 'Convênio' : '';
    return [
      a.patientName ?? '',
      csvDate(a.date),
      a.startTime,
      receivableProcedureLabel(a),
      ins ? 'Convênio' : 'Paciente',
      insurer,
      a.professionalId ? profNames[a.professionalId] ?? '' : '',
      csvDate(a.chargedAt),
      a.chargedByName ?? '',
      ...(mode === 'open'
        ? [
            csvDate(a.paymentDueDate),
            methodLabel(a.paymentAgreedMethod),
            openAmount(a) === undefined ? 'a definir' : csvMoney(openAmount(a)),
          ]
        : [
            csvDate(a.receivableSettledAt),
            methodLabel(a.receivableSettledMethod),
            a.receivableSettledByName ?? '',
            csvMoney(a.receivableSettledAmount),
          ]),
      a.receivableNote ?? '',
    ];
  });
  return '\uFEFF' + [head, ...rows].map((r) => r.map(csvCell).join(';')).join('\r\n');
}

